import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import ProductCard from "./Card";
import productAPI from "../../api/productApi";
import { normalizeProduct } from "../../utils/normalizeProduct";
import Breadcrump from "../../components/Breadcrump";

gsap.registerPlugin(ScrollTrigger);


// ─── Arrow Icon ───────────────────────────────────────────────────────────────
const ArrowRight = () => (
  <svg className="new-arrivals-arrow" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M1 6.5h11M7 1l5.5 5.5L7 12" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Get current month + year for the datestamp
const NOW       = new Date();
const MONTH_STR = NOW.toLocaleString("en-IN", { month: "long" });
const YEAR_STR  = NOW.getFullYear();

// ─── Component ────────────────────────────────────────────────────────────────
export default function NewArrivals() {
  const [products, setProducts] = useState([]);

  const eyebrowRef = useRef(null);
  const headingRef = useRef(null);
  const descRef    = useRef(null);
  const dateRef    = useRef(null);
  const gridRef    = useRef(null);
  const ctaRef     = useRef(null);

  // ─── Fetch new arrivals ───────────────────────────────────────────────────
  useEffect(() => {
    productAPI.listing({ sort: "newest", limit: 4 })
      .then((res) => {
        if (res.data?.products) {
          setProducts(res.data.products.map(normalizeProduct));
        }
      })
      .catch((err) => console.error("NewArrivals API error:", err));
  }, []);

  // ─── GSAP animations ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!products.length) return;
    if (typeof gsap === "undefined") return;

    const ctx = gsap.context(() => {

      // Header sequence
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: eyebrowRef.current,
          start: "top 82%",
          once: true,
        },
        defaults: { ease: "power3.out" },
      });

      tl
        .to(eyebrowRef.current, { opacity: 1, duration: 0.65 }, 0)
        .to(headingRef.current, { opacity: 1, y: 0, duration: 0.85 }, 0.18)
        .to(descRef.current,    { opacity: 1, duration: 0.65 }, 0.42)
        .to(dateRef.current,    { opacity: 1, duration: 0.55 }, 0.5);

      // Cards stagger
      if (gridRef.current) {
        gsap.to(gridRef.current.querySelectorAll(".pcard"), {
          opacity: 1,
          y: 0,
          duration: 0.65,
          stagger: 0.1,
          ease: "power3.out",
          scrollTrigger: {
            trigger: gridRef.current,
            start: "top 80%",
            once: true,
          },
        });
      }

      // CTA button
      gsap.to(ctaRef.current, {
        opacity: 1,
        duration: 0.55,
        ease: "power2.out",
        scrollTrigger: {
          trigger: ctaRef.current,
          start: "top 90%",
          once: true,
        },
      });

    });

    return () => ctx.revert();
  }, [products]); // products aane ke baad GSAP chalega — OurProducts jaisa

  if (!products.length) return null;

  return (
    <>

    <Breadcrump title={products?.name || "New Arrivals"} />
      <section className="new-arrivals-section" aria-labelledby="new-arrivals-heading">
        <div className="container">
          {/* ── Header Row ── */}
          <div className="row align-items-end new-arrivals-header">

            {/* Left — titles */}
            <div className="col-12 col-md-8">
              <p className="new-arrivals-eyebrow" ref={eyebrowRef}>
                <span className="new-arrivals-eyebrow-line" aria-hidden="true" />
                Just In
              </p>
              <h2 className="new-arrivals-heading" id="new-arrivals-heading" ref={headingRef}>
                New <em>Arrivals</em>
              </h2>
              <p className="new-arrivals-description" ref={descRef}>
                Discover the latest additions to our curated ethnic collection
                crafted with timeless elegance and heritage artistry.
              </p>
            </div>

            {/* Right — datestamp */}
            <div className="col-12 col-md-4 d-flex justify-content-md-end">
              <div className="new-arrivals-datestamp" ref={dateRef} aria-hidden="true">
                <span className="new-arrivals-datestamp-label">Season Drop</span>
                <span className="new-arrivals-datestamp-value">{MONTH_STR} {YEAR_STR}</span>
              </div>
            </div>

          </div>

          {/* ── Grid ── */}
          <div className="new-arrivals-grid" ref={gridRef} aria-label="New arrivals product listing">
            {products.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>

          {/* ── CTA Button ── */}
          <div className="new-arrivals-cta-row" ref={ctaRef}>
            <Link to="/shop" className="new-arrivals-cta-link">
              <span>View All New Arrivals</span>
              <ArrowRight />
            </Link>
          </div>

        </div>
      </section>
    </>
  );
}